import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { dataPortabilityService, DataPortabilityExportRequest } from '../services/dataPortabilityService'
import { patientService } from '../services/patientService'
import { Patient } from '../types/patient'
import { useQuery } from '@tanstack/react-query'
import './DataPortabilityExport.css'

export function DataPortabilityExport() {
  const [exportType, setExportType] = useState<'individual' | 'practice'>('individual')
  const [selectedPatientId, setSelectedPatientId] = useState<string>('')
  const [format, setFormat] = useState<'csv' | 'text'>('csv')
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch patients for patient selector
  const { data: patients = [] } = useQuery<Patient[]>({
    queryKey: ['patients', searchTerm],
    queryFn: async () => {
      const result = await patientService.getAll(searchTerm || undefined)
      return Array.isArray(result) ? result : result.patients || []
    },
    enabled: exportType === 'individual',
  })

  const exportMutation = useMutation({
    mutationFn: async (request: DataPortabilityExportRequest) => {
      await dataPortabilityService.exportDataPortability(request)
    },
    onSuccess: () => {
      alert('Export completed successfully!')
    },
    onError: (error: Error) => {
      alert(`Export failed: ${error.message}`)
    },
  })

  const handleExport = () => {
    if (exportType === 'individual' && !selectedPatientId) {
      alert('Please select a patient')
      return
    }

    const request: DataPortabilityExportRequest = {
      patientId: exportType === 'individual' ? selectedPatientId : undefined,
      format,
      includeFiles: true,
    }

    exportMutation.mutate(request)
  }

  return (
    <div className="data-portability-export-page">
      <div className="data-portability-header">
        <h1>Data Export</h1>
        <div className="admin-badge">Admin Only</div>
      </div>

      <div className="data-portability-info">
        <p>
          Export patient data in standard formats. Exports include all patient-related information including:
        </p>
        <ul>
          <li>Patient demographics and personal information</li>
          <li>Consultations and clinical notes</li>
          <li>Prescriptions and medications</li>
          <li>Lab results and vital signs</li>
          <li>Allergies and immunisations</li>
          <li>Appointments</li>
          <li>Clinical history</li>
          <li>Audit trail (all changes and access logs)</li>
          <li>Scanned documents and attached files (metadata)</li>
        </ul>
      </div>

      <div className="data-portability-form">
        <div className="form-group">
          <label className="form-label">Export Scope:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="exportType"
                value="individual"
                checked={exportType === 'individual'}
                onChange={(e) => setExportType(e.target.value as 'individual' | 'practice')}
              />
              <span>Individual Patient</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="exportType"
                value="practice"
                checked={exportType === 'practice'}
                onChange={(e) => setExportType(e.target.value as 'individual' | 'practice')}
              />
              <span>Entire Practice Population</span>
            </label>
          </div>
        </div>

        {exportType === 'individual' && (
          <div className="form-group">
            <label htmlFor="patient-select">Select Patient:</label>
            <div className="patient-selector">
              <input
                type="text"
                id="patient-search"
                placeholder="Search patients by name, number, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="patient-search-input"
              />
              <select
                id="patient-select"
                value={selectedPatientId}
                onChange={(e) => setSelectedPatientId(e.target.value)}
                className="patient-select"
              >
                <option value="">-- Select a patient --</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.patientNumber || patient.id.slice(0, 8)} - {patient.firstName}{' '}
                    {patient.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="format-select">Export Format:</label>
          <select
            id="format-select"
            value={format}
            onChange={(e) => setFormat(e.target.value as 'csv' | 'text')}
          >
            <option value="csv">CSV (Comma-Separated Values)</option>
            <option value="text">Text File (Human-Readable)</option>
          </select>
        </div>

        <div className="form-actions">
          <button
            className="btn-primary"
            onClick={handleExport}
            disabled={exportMutation.isPending || (exportType === 'individual' && !selectedPatientId)}
          >
            {exportMutation.isPending ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        {exportMutation.isPending && (
          <div className="export-progress">
            <p>Generating export... This may take a few moments for large datasets.</p>
          </div>
        )}
      </div>

      <div className="data-portability-notes">
        <h3>Important Notes:</h3>
        <ul>
          <li>
            <strong>CSV Format:</strong> See{' '}
            <a
              href="/docs/DATA_PORTABILITY_CSV_FORMAT.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              CSV Format Documentation
            </a>{' '}
            for complete field specifications, separators, and column definitions.
          </li>
          <li>
            <strong>Text Format:</strong> Human-readable format with sections for each data type.
          </li>
          <li>
            <strong>File Downloads:</strong> Document files are referenced by metadata. Actual file
            downloads may be provided separately if needed.
          </li>
          <li>
            <strong>Large Exports:</strong> Practice population exports may take several minutes to
            generate. Please be patient.
          </li>
          <li>
            <strong>Data Completeness:</strong> All exports include the complete audit trail and
            file metadata.
          </li>
        </ul>
      </div>
    </div>
  )
}
